export type BaseUrls = {
    graphApiUrl: string;
    engineApiUrl: string;
    cmsApiUrl: string;
    apiBaseUrl: string;
    gbifApiUrl: string;
    supportUrl: string;
    sentryDsn: string;
    shopifyRedirectUrl: string;
    cognitoLoginUrl: string;
    bookDemoCallFormUrl: string;
    uploadDataFormUrl: string;
};

/** The backdrop for the **MapLibre** maps, which draw the engine's site vector
 *  tiles. The Leaflet maps use `googleMapsJavascriptApiKey` instead: MapLibre
 *  cannot read the Google Maps JS SDK, so the two stacks need separate settings.
 *
 *  CAUTION: Set these only to a service this product is licensed to use. Most free
 *  public tile services carry usage policies that a commercial product breaks. */
export type MaplibreBasemapConfig = {
    /** A vector style to draw the estate over. */
    maplibreBasemapStyleUrl: string;
    maplibreBasemapAttribution: string;
};

export type HubSpotConfig = {
    hubSpotRegion: string;
    hubSpotPortalId: string;
};

export type CookiesConfig = {
    cookiesDomain: string;
    cookiesExpires: number;
    cookiesPath: string;
    cookiesSecure: boolean;
};

export type Keys = {
    cognitoUserPoolId: string;
    cognitoClientId: string;
    /** The **Leaflet** maps only. See MaplibreBasemapConfig for the MapLibre ones. */
    googleMapsJavascriptApiKey: string;
    googleAnalyticsKey: string;
    msClarityAnalyticsKey: string;
};

export type PublicKeys = {
    googleAnalyticsKey: string;
    msClarityAnalyticsKey: string;
};

const defaultUrls: BaseUrls = {
    graphApiUrl: 'https://api.dev.nm-digital.net/query',
    engineApiUrl: 'https://engine.staging.nm-digital.net',
    cmsApiUrl: 'https://cms.prod.nm-digital.net/api',
    apiBaseUrl: 'https://api.dev.nm-digital.net',
    gbifApiUrl: 'https://api.gbif.org/v1',

    supportUrl: 'https://share-eu1.hsforms.com/2D5R8j2gPR4u7eeDsb2tXxg2erc4a',
    sentryDsn: 'https://87916080e3f845ab908b40fe182348e5@o4505425811406848.ingest.sentry.io/4505442338734080',
    shopifyRedirectUrl: '',
    cognitoLoginUrl: '',
    bookDemoCallFormUrl: 'https://2erc4a.share-eu1.hsforms.com/2UeeVDxhTTIGYs8KD2lh3Bg',
    uploadDataFormUrl: 'https://2erc4a.share-eu1.hsforms.com/2SYFjA8GTQh--38uy6KE1ew',
};

const defaultKeys: Keys = {
    cognitoUserPoolId: '',
    cognitoClientId: '',
    googleMapsJavascriptApiKey: '',
    googleAnalyticsKey: '',
    msClarityAnalyticsKey: '',
};

// OpenFreeMap: free for commercial use, with no key, no registration and no request
// limit, under MIT with OpenStreetMap data.
const defaultMaplibreBasemapConfig: MaplibreBasemapConfig = {
    maplibreBasemapStyleUrl: 'https://tiles.openfreemap.org/styles/liberty',
    maplibreBasemapAttribution: 'OpenFreeMap © OpenMapTiles Data from OpenStreetMap',
};

const defaultHubSpotConfig: HubSpotConfig = {
    hubSpotRegion: 'eu1',
    hubSpotPortalId: '145722394',
};

const defaultCookiesConfig: CookiesConfig = {
    cookiesDomain: 'localhost',
    cookiesExpires: 7,
    cookiesPath: '/',
    cookiesSecure: false,
};

/**
 * Vite injects `import.meta.env` in app builds. Playwright runs tests in Node without that object, so we fall back to
 * `process.env` (e.g. `REACT_APP_*` from dotenv) when importing this module from e2e tests.
 */
const appEnv: ImportMetaEnv = (import.meta as ImportMeta).env ?? (process.env as unknown as ImportMetaEnv);

const getCookiesConfig = (): CookiesConfig => {
    return {
        cookiesDomain: appEnv.REACT_APP_COOKIES_DOMAIN ?? defaultCookiesConfig.cookiesDomain,
        cookiesExpires: appEnv.REACT_APP_COOKIES_EXPIRES_IN_DAYS
            ? parseInt(appEnv.REACT_APP_COOKIES_EXPIRES_IN_DAYS)
            : defaultCookiesConfig.cookiesExpires,
        cookiesPath: appEnv.REACT_APP_COOKIES_PATH ?? defaultCookiesConfig.cookiesPath,
        cookiesSecure: appEnv.REACT_APP_COOKIES_SECURE_FLAG
            ? appEnv.REACT_APP_COOKIES_SECURE_FLAG === 'true'
            : defaultCookiesConfig.cookiesSecure,
    };
};

const getBaseApiUrls = (): BaseUrls => {
    return {
        graphApiUrl: appEnv.REACT_APP_GRAPH_API_URL ?? defaultUrls.graphApiUrl,
        engineApiUrl: appEnv.REACT_APP_ENGINE_API_URL || defaultUrls.engineApiUrl,
        cmsApiUrl: appEnv.REACT_APP_CMS_API_URL ?? defaultUrls.cmsApiUrl,
        apiBaseUrl: appEnv.REACT_APP_API_BASE_URL ?? defaultUrls.apiBaseUrl,
        gbifApiUrl: appEnv.REACT_APP_GBIF_API_URL ?? defaultUrls.gbifApiUrl,
        supportUrl: appEnv.REACT_APP_SUPPORT_URL ?? defaultUrls.supportUrl,
        sentryDsn: appEnv.REACT_APP_SENTRY_DSN ?? defaultUrls.sentryDsn,
        shopifyRedirectUrl: appEnv.REACT_APP_SHOPIFY_REDIRECT_URL ?? defaultUrls.shopifyRedirectUrl,
        cognitoLoginUrl: appEnv.REACT_APP_COGNITO_LOGIN_URL ?? defaultUrls.cognitoLoginUrl,
        bookDemoCallFormUrl: appEnv.REACT_APP_BOOK_CALL_FORM_URL ?? defaultUrls.bookDemoCallFormUrl,
        uploadDataFormUrl: appEnv.REACT_APP_UPLOAD_DATA_FORM_URL ?? defaultUrls.uploadDataFormUrl,
    };
};

const getKeys = (): Keys => {
    return {
        cognitoUserPoolId: appEnv.REACT_APP_COGNITO_USER_POOL_ID ?? defaultKeys.cognitoUserPoolId,
        cognitoClientId: appEnv.REACT_APP_COGNITO_CLIENT_ID ?? defaultKeys.cognitoClientId,
        googleMapsJavascriptApiKey: appEnv.REACT_APP_GOOGLE_MAPS_JAVASCRIPT_API_KEY ?? defaultKeys.googleMapsJavascriptApiKey,
        googleAnalyticsKey: appEnv.REACT_APP_GOOGLE_ANALYTICS_KEY ?? defaultKeys.googleMapsJavascriptApiKey,
        msClarityAnalyticsKey: appEnv.REACT_APP_MS_CLARITY_KEY ?? defaultKeys.msClarityAnalyticsKey,
    };
};

const getMaplibreBasemapConfig = (): MaplibreBasemapConfig => {
    return {
        maplibreBasemapStyleUrl: appEnv.REACT_APP_MAPLIBRE_BASEMAP_STYLE_URL ?? defaultMaplibreBasemapConfig.maplibreBasemapStyleUrl,
        maplibreBasemapAttribution:
            appEnv.REACT_APP_MAPLIBRE_BASEMAP_ATTRIBUTION ?? defaultMaplibreBasemapConfig.maplibreBasemapAttribution,
    };
};

const getHubSpotConfig = (): HubSpotConfig => {
    return {
        hubSpotRegion: appEnv.REACT_APP_HUBSPOT_REGION ?? defaultHubSpotConfig.hubSpotRegion,
        hubSpotPortalId: appEnv.REACT_APP_HUBSPOT_PORTAL_ID ?? defaultHubSpotConfig.hubSpotPortalId,
    };
};

const deploymentEnv = appEnv.REACT_APP_DEPLOYMENT_ENV ?? 'localhost';

const portfolioClientSideThreshold = Number(appEnv.REACT_APP_PORTFOLIO_CLIENT_SIDE_THRESHOLD ?? '50');

export default {
    // Vite exposes `DEV`/`PROD` on import.meta.env and does not set NODE_ENV there,
    // so the NODE_ENV check alone was always false in the browser — which quietly
    // disabled the Redux devtools locally. NODE_ENV stays as the fallback for the
    // Playwright/Node path, where this module reads process.env instead.
    isDevelopment: appEnv.DEV === true || appEnv.NODE_ENV === 'development',
    isProduction: !(appEnv.DEV === true || appEnv.NODE_ENV === 'development'),
    shouldReportErrors: ['dev', 'staging', 'prod'].includes(deploymentEnv),
    deploymentEnv,
    portfolioClientSideThreshold,
    ...getBaseApiUrls(),
    ...getKeys(),
    ...getMaplibreBasemapConfig(),
    ...getCookiesConfig(),
    ...getHubSpotConfig(),
};
